import ApiClient from 'api-client/ApiClient'
import { getDomainId, getIDPName } from 'api-client/helpers'
import { defaultGroupMappingId } from 'app/constants'
import {
  addIdp,
  deleteFederatedUsers,
  deleteGroup,
  deleteGroupMapping,
  deleteIdp,
  deleteSsoConfig,
  updateIdp,
} from 'app/plugins/account/components/ssoManagement/sso/actions'
import { DeletionStep } from 'app/plugins/account/components/ssoManagement/sso/DeletionProgress'
import { SsoProviders } from 'app/plugins/account/components/ssoManagement/sso/model'
import { extractGroupIdsForMapping } from 'app/plugins/account/components/ssoManagement/utils/mappingUtils'

export enum DELETION_STATUS {
  IDLE = 'idle',
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export const createSsoConfigBody = (params) => ({
  provider: params.ssoProvider === SsoProviders.Other ? params.ssoProviderName : params.ssoProvider,
  entity_id: params.entityId,
  metadata_url: params.metadataUrl,
  // metadata: params.metadataUrl ? undefined : params.metadata,
  attr_map_xml: params.defaultAttributeMap,
  domain_name: getIDPName(),
})

export const handleIdpConfiguration = async (hasIdp, params) => {
  if (hasIdp) {
    await updateIdp({
      identity_provider: {
        remote_ids: [params.entityId],
      },
    })
  } else {
    await addIdp({
      identity_provider: {
        remote_ids: [params.entityId],
        description: null,
        domain_id: getDomainId(),
        enabled: true,
      },
    })
  }
}

export const getErrorMsgFromApiError = (error) => {
  const msg = error?.response?.data?.match(/"msg"\s*:\s*"([^"]*)"/)
  return msg
    ? `Failed to update SSO configuration. ${msg[1]}`
    : 'Failed to update SSO configuration. Please try again.'
}

const safeDeleteIdp = async () => {
  const { keystone } = ApiClient.getInstance()

  try {
    // Check if IDP exists before Delete
    const existingIdps = await keystone.getIdentityProviders()

    if (!existingIdps?.length) {
      // IDP already gone - mark as success
      return
    }

    await deleteIdp()
  } catch (err) {
    // If backend returns “not found”, also mark as success
    if (err?.message?.includes('Could not find Identity Provider')) return
    throw err // otherwise, throw the error to fail the step
  }
}

export const deleteGroupsAndMapping = async (step: 4 | 5) => {
  // Deletes groups (step 4) or group mapping (step 5) for the SSO provider,
  // fetching mappings once to avoid duplicate API calls and ensure retry safety.
  const { keystone } = ApiClient.getInstance()

  const mappings = await keystone.getGroupMappings()
  if (!mappings?.length) return
  const mappingId = defaultGroupMappingId(getIDPName())
  const groupIds = extractGroupIdsForMapping(mappings, mappingId)

  if (step === 4) {
    // Step 4: Delete groups
    const results = await Promise.allSettled(groupIds?.map((id) => deleteGroup(id)))
    if (results?.some((r) => r?.status === 'rejected'))
      throw new Error('Failed to delete some groups')
  }

  if (step === 5) {
    // Step 5: Delete group mapping
    await deleteGroupMapping(mappingId)
  }
}

export const defaultDeleteProgressDetails: DeletionStep[] = [
  { step: 1, status: 'idle', label: 'Delete SSO Configuration' },
  { step: 2, status: 'idle', label: 'Delete Identity Provider' },
  { step: 3, status: 'idle', label: 'Delete Federated Users' },
  { step: 4, status: 'idle', label: 'Delete Groups' },
  { step: 5, status: 'idle', label: 'Delete Group Mappings' },
]

export const ssoCleanupSteps = [
  {
    step: 1,
    label: 'Delete SSO Configuration',
    action: () => deleteSsoConfig(),
    defaultErrorMessage: 'Failed to delete SSO configuration',
  },
  {
    step: 2,
    label: 'Delete Identity Provider',
    action: safeDeleteIdp,
    defaultErrorMessage: 'Failed to delete Identity Provider',
  },
  {
    step: 3,
    label: 'Delete Federated Users',
    action: () => deleteFederatedUsers(),
    defaultErrorMessage: 'Failed to delete federated users',
  },
  {
    step: 4,
    label: 'Delete Groups',
    action: () => deleteGroupsAndMapping(4),
    defaultErrorMessage: 'Failed to delete groups',
  },
  {
    step: 5,
    label: 'Delete Group Mappings',
    action: () => deleteGroupsAndMapping(5),
    defaultErrorMessage: 'Failed to delete group mappings',
  },
]
const FEDERATED_USER_ID_LENGTH = 64 // UUID length

export const isFederatedUser = (user) =>
  user?.id?.length === FEDERATED_USER_ID_LENGTH && !user?.default_project_id
